/**
 * Account settings writes: coach/client settings, profile name, password.
 * All go through the global apiClient (CSRF, credentials, 429 handling, Sentry).
 *
 * saveSettings/updateProfile return the non-throwing ApiResponse; useSettings turns failures into
 * toasts using the server's `message` (or its own fallback copy). changePassword keeps its
 * long-standing `{ ok, message }` contract for the settings screens.
 */

import { api, type ApiResponse } from '@/lib/apiClient';

export function settingsEndpoint(role: 'coach' | 'client'): string {
  return role === 'coach' ? '/api/coach/settings' : '/api/clients/settings';
}

export function saveSettings<T>(
  role: 'coach' | 'client',
  updates: Partial<T>,
): Promise<ApiResponse & { settings?: Partial<T> }> {
  return api.post(settingsEndpoint(role), updates);
}

export function updateProfile(updates: { name: string }): Promise<ApiResponse & { profile?: { full_name?: string } }> {
  return api.post('/api/auth/update-profile', updates);
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: boolean; message: string }> {
  const res = await api.post('/api/auth/change-password', { currentPassword, newPassword });
  // No status = never reached the server (network error / timeout): show apiClient's text.
  if (!res.ok && !res.status) return { ok: false, message: res.error || 'Network error' };
  return { ok: res.ok, message: res.message || (res.ok ? 'Password updated' : 'Failed to change password') };
}
